import { Link, useParams } from "react-router-dom";
import AdminAvaliacoes from "./AdminAvaliacoes";
import AdminFeedbacksPesquisas from "./AdminFeedbacksPesquisas";

/**
 * Admin › **Feedbacks** (000332) — o que os alunos dizem, numa aba só e em duas
 * seções separadas. Pedido de 30/09: *"Unificar pesquisas de satisfação com
 * avaliações, devem morar na mesma aba do Painel. Aba 'Feedbacks'. Aí separa a
 * visualização das pesquisas e dos feedbacks de ferramentas."*
 *
 *   · **Avaliações das ferramentas** — o painel da 000327/000328, com as quatro
 *     sub-abas da Central Feedbacks e o período. É o padrão da aba, e os
 *     endereços dele não mudaram (`/admin/avaliacoes`, `/feedbacks`,
 *     `/ferramentas`, `/comparar`): os links mandados a um colega continuam
 *     abrindo;
 *   · **Pesquisas de satisfação** — as pesquisas por curso (000310), que
 *     moravam na página do produto no Catálogo. Endereço novo:
 *     `/admin/avaliacoes/pesquisas`.
 *
 * O endereço da aba continua `/admin/avaliacoes`, e a chave dela na matriz
 * também (`avaliacoes`): só o NOME virou Feedbacks. Trocar a chave mexeria nas
 * funções, nas concessões e nos testes por um ganho que é só de nome — os dois
 * nomes convivem, como `pos_turma_corrente` e "turma atual".
 *
 * A troca de seção é desenhada NA LINHA DO TÍTULO de cada seção, e não acima
 * dele: a Visão geral das avaliações tem de caber na primeira dobra a 1280px, e
 * medido em 30/09 sobravam 18px na janela real. Uma faixa acima do título a
 * empurraria para baixo da dobra.
 */
export default function AdminFeedbacks({ podeEditar, veCatalogo }: { podeEditar: boolean; veCatalogo: boolean }) {
  const { sub } = useParams();
  if (sub === "pesquisas") {
    return (
      <AdminFeedbacksPesquisas podeEditar={podeEditar} veCatalogo={veCatalogo} secoes={<SecoesDosFeedbacks atual="pesquisas" />} />
    );
  }
  // Toda outra sub-rota é do painel das avaliações, que decide o que fazer com ela.
  return <AdminAvaliacoes podeEditar={podeEditar} veCatalogo={veCatalogo} secoes={<SecoesDosFeedbacks atual="ferramentas" />} />;
}

const SECOES = [
  { id: "ferramentas", rotulo: "Avaliações das ferramentas", curto: "Avaliações", caminho: "/admin/avaliacoes" },
  { id: "pesquisas", rotulo: "Pesquisas de satisfação", curto: "Pesquisas", caminho: "/admin/avaliacoes/pesquisas" },
] as const;

/**
 * As duas seções, como controle SEGMENTADO: é um nível acima das sub-abas das
 * avaliações (as pílulas laranja), e por isso tem outro desenho. A escolhida usa
 * o `acento`, o azul de interface, que é visível nos dois temas.
 *
 * O link leva só à seção: o período das avaliações não significa nada nas
 * pesquisas, e volta ao padrão quando se retorna. Tem a ALTURA do título (32px), e
 * é por isso que o contorno é um anel (`ring`), que não ocupa espaço: com a
 * borda ela tinha 34px, e a linha do título crescia 2px — medido em 30/09, quando
 * a folga do título da atenção na janela real tinha chegado a zero. Abaixo de 640px o rótulo é o CURTO: os dois
 * longos lado a lado passam de 400px e estourariam a largura do celular.
 */
function SecoesDosFeedbacks({ atual }: { atual: (typeof SECOES)[number]["id"] }) {
  return (
    <nav aria-label="Seções da aba Feedbacks" data-secoes-dos-feedbacks>
      <ul className="inline-flex rounded-full bg-superficie p-0.5 ring-1 ring-borda ring-inset">
        {SECOES.map((s) => (
          <li key={s.id}>
            <Link
              to={s.caminho}
              aria-current={atual === s.id ? "page" : undefined}
              data-secao-dos-feedbacks={s.id}
              className={`inline-flex items-center rounded-full px-3.5 py-1 text-sm font-semibold whitespace-nowrap transition ${
                atual === s.id ? "bg-acento text-acento-tinta" : "text-cinza hover:bg-chip hover:text-tinta"
              }`}
            >
              <span className="sm:hidden">{s.curto}</span>
              <span className="hidden sm:inline">{s.rotulo}</span>
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
